import React from 'react';

import useModal from '../hooks/useModal';
import {
  AddAccountModal,
  AddDentistModal,
  OverviewModal
} from '@/components/modals';

interface SelectDentistModalsProps {
  // Modal control props
  modals: {
    overview: { isOpen: boolean; onOpen: () => void; onClose: () => void };
    addDentist: { isOpen: boolean; onOpen: () => void; onClose: () => void };
    addAccount: { isOpen: boolean; onOpen: () => void; onClose: () => void };
  };
}

/**
 * Higher-Order Component that provides all select dentist-related modals with their state management.
 *
 * This HOC wraps a component and provides modal state and controls as props.
 * It handles all modal state internally and renders the modal components.
 *
 * @param WrappedComponent - The component to wrap with modal functionality
 * @returns Enhanced component with modal props
 */
const withSelectDentistModals = <P extends SelectDentistModalsProps>(
  WrappedComponent: React.ComponentType<P>
) => {
  const ComponentWithSelectDentistModals = (
    props: Omit<P, keyof SelectDentistModalsProps>
  ) => {
    // Modal states
    const overviewModal = useModal();
    const addDentistModal = useModal();
    const addAccountModal = useModal();

    // Modal controls object
    const modals = {
      overview: {
        isOpen: overviewModal.isOpen,
        onOpen: overviewModal.onOpen,
        onClose: overviewModal.onClose
      },
      addDentist: {
        isOpen: addDentistModal.isOpen,
        onOpen: addDentistModal.onOpen,
        onClose: addDentistModal.onClose
      },
      addAccount: {
        isOpen: addAccountModal.isOpen,
        onOpen: addAccountModal.onOpen,
        onClose: addAccountModal.onClose
      }
    };

    return (
      <>
        <WrappedComponent {...(props as P)} modals={modals} />

        {/* Modal Components */}
        <OverviewModal
          isOpen={overviewModal.isOpen}
          onClose={overviewModal.onClose}
          presentingElement={undefined}
        />
        <AddDentistModal
          isOpen={addDentistModal.isOpen}
          onClose={addDentistModal.onClose}
          presentingElement={undefined}
        />
        <AddAccountModal
          isOpen={addAccountModal.isOpen}
          onClose={addAccountModal.onClose}
          presentingElement={undefined}
        />
      </>
    );
  };

  ComponentWithSelectDentistModals.displayName = `withSelectDentistModals(${WrappedComponent.displayName || WrappedComponent.name})`;

  return ComponentWithSelectDentistModals;
};

export default withSelectDentistModals;
